import { canAccess } from '@/core/security/authorization';
import type { SidebarMenuItem } from '../types';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Accepts whatever the API returns and yields only well-formed items. Menu data
 * is remote input: a malformed entry must drop out, not blank the sidebar.
 * Trailing whitespace in labels is trimmed (the comp's own labels have it).
 */
export function sanitizeItems(raw: unknown): SidebarMenuItem[] {
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const items: SidebarMenuItem[] = [];

  raw.forEach((entry, index) => {
    if (typeof entry !== 'object' || entry === null) return;
    const candidate = entry as Partial<SidebarMenuItem>;

    if (!isNonEmptyString(candidate.id)) return;
    if (!isNonEmptyString(candidate.label)) return;
    if (!isNonEmptyString(candidate.path)) return;
    if (seen.has(candidate.id)) return;
    seen.add(candidate.id);

    const children = sanitizeItems(candidate.children);

    items.push({
      id: candidate.id,
      label: candidate.label.trim(),
      path: candidate.path,
      order: Number.isFinite(candidate.order) ? Number(candidate.order) : index * 10,
      ...(isNonEmptyString(candidate.icon) ? { icon: candidate.icon } : {}),
      ...(candidate.disabled === true ? { disabled: true } : {}),
      ...(Array.isArray(candidate.requiredRoles)
        ? { requiredRoles: candidate.requiredRoles.filter(isNonEmptyString) }
        : {}),
      /* Carried through so a served menu can name its feature exactly as a static one does. */
      ...(isNonEmptyString(candidate.feature) ? { feature: candidate.feature.trim() } : {}),
      ...(isNonEmptyString(candidate.requiredPermission)
        ? { requiredPermission: candidate.requiredPermission }
        : {}),
      ...(children.length > 0 ? { children } : {}),
    });
  });

  return items.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

/**
 * Drops items the user's roles do not allow.
 *
 * A parent stays visible even when every child is filtered out (section 9.1),
 * but loses its `children` so it renders as a plain link rather than an
 * expander that opens onto nothing.
 */
export function filterByRoles(
  items: readonly SidebarMenuItem[],
  userRoles: readonly string[],
): SidebarMenuItem[] {
  return items.reduce<SidebarMenuItem[]>((visible, item) => {
    if (!canAccess(userRoles, item.requiredRoles)) return visible;

    const children = item.children ? filterByRoles(item.children, userRoles) : [];
    const { children: _dropped, ...rest } = item;

    visible.push(children.length > 0 ? { ...rest, children } : rest);
    return visible;
  }, []);
}

/**
 * Drops items whose permission the session does not hold.
 *
 * Roles decide which shell a user gets; a permission decides whether one screen
 * inside it is theirs. Workflow Configuration is the first of those: the platform
 * grants it independently of the Admin role, so an Admin can hold the role and
 * not the code — and before this, the sidebar drew the entry anyway and clicking
 * it landed on /unauthorized. A link the app itself drew must not do that.
 *
 * An item with no `requiredPermission` is unaffected, which is every existing one.
 *
 * `held` is passed in rather than read here so this stays a pure function: the
 * codes live in the access token, and reading storage from a util would make the
 * whole menu untestable without one.
 */
export function filterByPermissions(
  items: readonly SidebarMenuItem[],
  held: readonly string[],
): SidebarMenuItem[] {
  return items.reduce<SidebarMenuItem[]>((visible, item) => {
    if (item.requiredPermission !== undefined && !held.includes(item.requiredPermission)) {
      return visible;
    }

    const children = item.children ? filterByPermissions(item.children, held) : [];
    const { children: _dropped, ...rest } = item;

    visible.push(children.length > 0 ? { ...rest, children } : rest);
    return visible;
  }, []);
}
